import React from 'react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { Paginator } from 'primereact/paginator'
import { Chip } from 'primereact/chip'
import { Rating } from 'primereact/rating'
import { DataView } from 'primereact/dataview'
import { Toolbar } from 'primereact/toolbar'
import { InputText } from 'primereact/inputtext'
import { fetchAllShops, Shop } from '../api/shops'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/store'
import { setLanguage } from '../store/slices/languageSlice'
import { toggleMode } from '../store/slices/themeSlice'
import ClassicAppBar from '../components/ClassicAppBar'
import i18n from '../i18n/i18n'

const ShopsPage: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme.mode)
  const [items, setItems] = React.useState<Shop[]>([])
  const [loading, setLoading] = React.useState(true)
  const [first, setFirst] = React.useState(0)
  const [rows, setRows] = React.useState(12)
  const [q, setQ] = React.useState('')
  const [selectedCountry, setSelectedCountry] = React.useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null)
  const navigate = useNavigate()

  React.useEffect(() => {
    const loadShops = async () => {
      try {
        setLoading(true)
        const data = await fetchAllShops()
        setItems(data)
      } catch (error) {
        console.error('Failed to load shops:', error)
        setItems([])
      } finally {
        setLoading(false)
      }
    }
    loadShops()
  }, [])

  const filteredItems = React.useMemo(() => {
    let filtered = items

    if (q.trim()) {
      const query = q.toLowerCase()
      filtered = filtered.filter(x =>
        x.name.toLowerCase().includes(query) ||
        x.nameTamil?.includes(q) ||
        x.description?.toLowerCase().includes(query) ||
        x.city?.toLowerCase().includes(query)
      )
    }

    if (selectedCountry) {
      filtered = filtered.filter(x => x.country === selectedCountry)
    }

    if (selectedCategory) {
      filtered = filtered.filter(x => x.category === selectedCategory)
    }

    return filtered
  }, [items, q, selectedCountry, selectedCategory])

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  const onPageChange = (event: { first: number, rows: number }) => {
    setFirst(event.first)
    setRows(event.rows)
  }

  const itemTemplate = (shop: Shop) => {
    return (
      <div className="col-12 md:col-6 lg:col-4 xl:col-3 p-3">
        <div
          className="h-full border-round-3xl overflow-hidden surface-card relative"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(211, 84, 0, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.4s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)'
            e.currentTarget.style.boxShadow = '0 24px 48px rgba(211, 84, 0, 0.25)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)'
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
          onClick={() => navigate(`/shops/${shop.id}`)}
        >
          {/* Image */}
          <div className="relative overflow-hidden" style={{ height: '220px' }}>
            <img
              src={shop.imageUrl}
              alt={shop.name}
              className="w-full h-full object-cover"
              style={{ transition: 'transform 0.6s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)' }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
            />
            <div
              className="absolute bottom-0 left-0 w-full h-full"
              style={{
                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 60%)'
              }}
            />
            {/* Category Badge */}
            <div className="absolute top-3 right-3">
              <Chip
                label={i18n.language === 'ta' ? (shop.categoryTamil || shop.category || shop.type) : i18n.language === 'de' ? (shop.categoryGerman || shop.category || shop.type) : (shop.category || shop.type)}
                className="font-bold shadow-4 text-xs"
                style={{
                  background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                  color: 'white'
                }}
              />
            </div>
            {shop.isVerified && (
              <div className="absolute top-3 left-3">
                <Chip
                  label="Verified"
                  icon="pi pi-check"
                  className="font-bold shadow-4 text-xs"
                  style={{
                    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                    color: 'white'
                  }}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-4">
            <h3
              className="text-xl font-bold text-900 mb-2 line-height-3"
              style={{
                fontFamily: 'serif',
                background: 'linear-gradient(135deg, #1a252f 0%, #d35400 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                minHeight: '60px'
              }}
            >
              {i18n.language === 'ta' ? (shop.nameTamil || shop.name) : i18n.language === 'de' ? (shop.nameGerman || shop.name) : shop.name}
            </h3>

            <div className="flex align-items-center gap-2 mb-3">
              <i className="pi pi-map-marker text-orange-500 text-sm"></i>
              <span className="text-sm text-600">{i18n.language === 'ta' ? (shop.cityTamil || shop.city) : i18n.language === 'de' ? (shop.cityGerman || shop.city) : shop.city}{shop.country ? `, ${shop.country}` : ''}</span>
            </div>

            {shop.rating !== undefined && (
              <div className="flex align-items-center gap-2 mb-3">
                <Rating value={shop.rating} readOnly cancel={false} className="text-sm" />
                <span className="text-xs text-500">({shop.reviewCount || 0})</span>
              </div>
            )}

            <p className="text-600 line-height-3 text-sm mb-3" style={{ minHeight: '40px' }}>
              {i18n.language === 'ta' ? (shop.descriptionTamil || shop.description || 'விபரம் இல்லை') : i18n.language === 'de' ? (shop.descriptionGerman || shop.description || 'Keine Beschreibung') : (shop.description || 'Discover quality products and services')}
            </p>

            <div className="flex align-items-center gap-2 mb-4">
              <i className="pi pi-clock text-orange-500 text-sm"></i>
              <span className="text-xs text-600">
                {i18n.language === 'ta' ? (shop.openingHoursTamil || shop.openingHours || 'நேரம் இல்லை') : i18n.language === 'de' ? (shop.openingHoursGerman || shop.openingHours || 'Nicht verfügbar') : (shop.openingHours || 'Hours not available')}
              </span>
            </div>

            <Button
              label={t('common.viewDetails') || 'View Details'}
              icon="pi pi-arrow-right"
              iconPos="right"
              className="w-full border-round-3xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                border: 'none',
                padding: '0.65rem'
              }}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/shops/${shop.id}`)
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  const countries = [
    { label: 'All Countries', value: null },
    { label: 'Canada', value: 'Canada' },
    { label: 'Germany', value: 'Germany' },
    { label: 'United Kingdom', value: 'United Kingdom' },
    { label: 'Sri Lanka', value: 'Sri Lanka' }
  ]

  const categories = [
    { label: 'All Categories', value: null },
    { label: 'Grocery & Spices', value: 'Grocery & Spices' },
    { label: 'Fashion & Textiles', value: 'Fashion & Textiles' },
    { label: 'Restaurant & Food', value: 'Restaurant & Food' },
    { label: 'Books & Education', value: 'Books & Education' },
    { label: 'Jewelry & Accessories', value: 'Jewelry & Accessories' },
    { label: 'Health & Wellness', value: 'Health & Wellness' },
    { label: 'Community & Services', value: 'Community & Services' }
  ]

  return (
    <div className="min-h-screen flex flex-column">
      <Helmet>
        <title>{t('site.shops')} · YarlHub</title>
        <meta name="description" content="Tamil shops directory" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : undefined} />
      </Helmet>

      <ClassicAppBar
        mode={themeMode}
        onToggleMode={handleToggleMode}
        onChangeLang={handleLanguageChange}
      />

      {/* Hero Section */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a252f 0%, #2c3e50 50%, #1a252f 100%)',
          paddingTop: '3rem',
          paddingBottom: '3rem'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(211, 84, 0, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(211, 84, 0, 0.2) 0%, transparent 50%)',
            width: '100%',
            height: '100%'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-1" style={{ maxWidth: '1600px' }}>
          <div className="text-center">
            <div
              className="inline-flex align-items-center justify-content-center mb-3"
              style={{
                background: 'linear-gradient(135deg, rgba(211, 84, 0, 0.2), rgba(230, 126, 34, 0.2))',
                padding: '0.75rem 1.5rem',
                borderRadius: '50px',
                border: '2px solid rgba(211, 84, 0, 0.3)'
              }}
            >
              <i className="pi pi-shopping-bag text-orange-500 text-2xl mr-2"></i>
              <span className="text-white font-bold text-lg">{t('site.shops')}</span>
            </div>

            <h1
              className="text-white font-bold mb-3"
              style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontFamily: 'serif',
                lineHeight: '1.2',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
            >
              Tamil Shops Directory
            </h1>

            <p
              className="text-white-alpha-90 mx-auto text-lg line-height-3"
              style={{ maxWidth: '650px' }}
            >
              Discover authentic Tamil shops, restaurants, and services worldwide
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-6 relative surface-ground">
        <div className="mx-auto px-4" style={{ maxWidth: '1600px' }}>
          {loading ? (
            <div className="flex justify-content-center p-8">
              <div className="text-center">
                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl mb-3"></i>
                <div className="text-orange-500 text-xl font-semibold">{t('loading')}...</div>
              </div>
            </div>
          ) : (
            <>
              {/* Filters Toolbar */}
              <Toolbar
                className="mb-5"
                style={{
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(211, 84, 0, 0.1)',
                  borderRadius: '1rem',
                  padding: '1.5rem 2rem'
                }}
                start={
                  <div className="flex align-items-center gap-3">
                    <i className="pi pi-filter text-orange-500 text-2xl"></i>
                    <span className="font-bold text-900 text-lg">Filters</span>
                  </div>
                }
                center={
                  <div className="flex flex-wrap align-items-center gap-3" style={{ flex: 1 }}>
                    <span className="p-input-icon-left">
                      <i className="pi pi-search" />
                      <InputText
                        value={q}
                        onChange={(e) => { setFirst(0); setQ(e.target.value) }}
                        placeholder="Search Shops"
                        style={{ width: '280px' }}
                      />
                    </span>

                    <Dropdown
                      value={selectedCountry}
                      options={countries}
                      onChange={(e) => { setFirst(0); setSelectedCountry(e.value) }}
                      placeholder="Country"
                      style={{ width: '200px' }}
                      showClear
                    />

                    <Dropdown
                      value={selectedCategory}
                      options={categories}
                      onChange={(e) => { setFirst(0); setSelectedCategory(e.value) }}
                      placeholder="Category"
                      style={{ width: '200px' }}
                      showClear
                    />
                  </div>
                }
                end={
                  <Button
                    label="Clear All"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={() => {
                      setQ('')
                      setSelectedCountry(null)
                      setSelectedCategory(null)
                      setFirst(0)
                    }}
                  />
                }
              />

              {/* Results Count */}
              <div className="mb-4">
                <p className="text-600 text-lg">
                  <span className="font-bold text-orange-600">{filteredItems.length}</span> shops found
                </p>
              </div>

              {/* Shops Grid */}
              <DataView
                value={filteredItems.slice(first, first + rows)}
                itemTemplate={itemTemplate}
                paginator={false}
                className="w-full"
                layout="grid"
              />

              <Paginator
                first={first}
                rows={rows}
                totalRecords={filteredItems.length}
                onPageChange={onPageChange}
                className="mt-4 border-none bg-transparent"
              />
            </>
          )}
        </div>
      </div>

      <footer
        className="py-6 mt-auto"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderTop: '1px solid rgba(211, 84, 0, 0.2)'
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <p className="text-white-alpha-90 text-sm font-semibold">
            © 2024 YarlHub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default ShopsPage
